import { useCallback, useState, useMemo, useEffect, useRef } from 'react'
import { useGetAnimalList } from 'src/hooks/api/animal'

interface UseAnimalSelectProps {
  currentAnimalId?: string
  sexFilter?: 'Macho' | 'Fêmea'
  selectId?: string
}

type paramsType = {
  searchFields?: string
  search?: string
  sexo?: 'Macho' | 'Fêmea'
}

export const useAnimalSelect = ({ sexFilter, selectId }: UseAnimalSelectProps) => {
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [isPaginationExhausted, setIsPaginationExhausted] = useState(false)

  const debounceTimer = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current)
    }

    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(search)
    }, 400)

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current)
      }
    }
  }, [search])

  const queryParams = useMemo(() => {
    let params: paramsType = {}

    if (sexFilter) {
      params = {
        ...params,
        sexo: sexFilter
      }
    }

    if (debouncedSearch) {
      params = {
        ...params,
        searchFields: 'nome,brinco',
        search: debouncedSearch
      }
    }

    return params
  }, [debouncedSearch])

  const scope =
    sexFilter && selectId
      ? `animal-list-select-${sexFilter}-${selectId}`
      : sexFilter
        ? `animal-list-select-${sexFilter}`
        : 'animal-list-select-all'

  const cacheTime = 0

  const {
    data: animalListData,
    isLoading,
    fetchNextPage,
    isFetchingNextPage
  } = useGetAnimalList(scope, queryParams, cacheTime)

  const animalList = (animalListData?.pages.flatMap((page) => page.list) ?? []).filter(
    (animal) => animal.categoria !== 'VD' && animal.categoria !== 'MT'
  )

  useEffect(() => {
    setIsPaginationExhausted(false)
  }, [scope, debouncedSearch])

  useEffect(() => {
    if (!animalListData?.pages?.length) return

    const lastPage = animalListData.pages[animalListData.pages.length - 1]
    if (lastPage?.nextPage && Array.isArray(lastPage.list) && lastPage.list.length === 0) {
      setIsPaginationExhausted(true)
    }
  }, [animalListData])

  const hasMorePages = useMemo(() => {
    if (!animalListData?.pages?.length) return false
    const lastPage = animalListData.pages[animalListData.pages.length - 1]
    return !!lastPage?.nextPage && !isPaginationExhausted
  }, [animalListData, isPaginationExhausted])

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value)
  }, [])

  const handleFetchMore = useCallback(async () => {
    if (!hasMorePages || isFetchingNextPage) return

    const currentSize = animalList.length
    const result = await fetchNextPage()
    const pages = result.data?.pages ?? []
    const nextSize = pages.reduce((count, page) => count + (page.list?.length ?? 0), 0)
    const lastPage = pages.length ? pages[pages.length - 1] : null

    if (nextSize <= currentSize || (lastPage?.nextPage && (lastPage.list?.length ?? 0) === 0)) {
      setIsPaginationExhausted(true)
    }
  }, [hasMorePages, isFetchingNextPage, animalList.length, fetchNextPage])

  return {
    animalList: animalList,
    isLoadingMore: isFetchingNextPage,
    isFiltering: isLoading,
    hasNextPage: hasMorePages,
    onSearchChange: handleSearchChange,
    onFetchMore: handleFetchMore
  }
}
